import {
    Autocomplete,
    Box,
    FormControl,
    FormHelperText,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useTranslation } from 'react-i18next';
import { ButtonComponent, CommonModal, FunctionsEnum, TextFieldComponent, colors } from '@watter/shared-components';
import { ErrorMessage, Form, Formik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { userEnums } from '@watter/shared-types';
import { GetCountries, GetState } from 'react-country-state-city';
import { useNavigate } from 'react-router-dom';
import { AntSwitch } from '../../components/common-styles';
import { MainWrapper } from '../common-styles';
import { hideLoader, showLoader } from '../../store/reducers/appSlice';
import { GET_INSTALLER_DETAILS } from '../../graphql/queries';
import { client } from '../../service/apolloClient';
import UpdateInstallerValidationSchema from './update-installer-validation-schema';
import { UPDATE_INSTALLER } from '../../graphql/mutations';
import { openToast } from '../../store/reducers/toastSlice';
import { usePermissions } from '../../custom_hooks/permissions';
import { NavigationConfig } from '../../utils/navigation-constants';
import { IFormObj } from './type';

interface UpdateInstallerProps {
    onClose: () => void;
    installer: any;
    fetchMangeInstallerData: () => void;
    open: boolean;
}

export const UpdateInstaller: React.FC<UpdateInstallerProps> = ({
    onClose,
    installer,
    fetchMangeInstallerData,
    open,
}) => {
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [installerData, setInstallerData] = useState<IFormObj | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [newStatus, setNewStatus] = useState<any>(installer?.status || userEnums.Status.inactive);

    const [countriesList, setCountriesList] = useState<any[]>([]);
    const [selectedCountryPhoneCode, setSelectedCountryPhoneCode] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [stateList, setStateList] = useState([]);
    const [selectedCountryId, setCountryId] = useState(0);
    const [selectedStateId, setStateId] = useState(0);
    const [userCountryCode, setUserCountryCode] = useState('');
    const [userStateCode, setUserStateCode] = useState('');
    const { canEdit, canView } = usePermissions();
    const navigate = useNavigate();
    useEffect(() => {
        if (scrollRef?.current) {
            scrollRef.current.scrollTop = 0;
        }
    }, [open]);
    const installerDetails = React.useCallback(async () => {
        try {
            dispatch(showLoader());

            const { data } = await client.query({
                query: GET_INSTALLER_DETAILS,
                variables: { getInstallerDetailsId: installer?.id },
                fetchPolicy: 'network-only',
            });

            if (data?.getInstallerDetails) {
                const fetchedInstaller = data.getInstallerDetails;
                const address = fetchedInstaller.user_addresses?.[0] || {};
                setUserCountryCode(address.country);
                setUserStateCode(address.state);
                setInstallerData({
                    firstName: fetchedInstaller.first_name,
                    lastName: fetchedInstaller.last_name,
                    licenseNo: fetchedInstaller.installer_users[0]?.license_number || '',
                    mobileNo: fetchedInstaller.phone_number,
                    email: fetchedInstaller.email,
                    status: fetchedInstaller.status,
                    cognitoId: fetchedInstaller.cognito_id,
                    id: fetchedInstaller.id,
                    city: address.city,
                    street: address.street,
                    zipCode: address.zip_code,
                });
            }
        } catch (err) {
            console.error('Error fetching installer details:', err);
        } finally {
            dispatch(hideLoader());
        }
    }, [dispatch, installer?.id]);
    useEffect(() => {
        const data: any = countriesList.filter((i: any) => i.iso2 === userCountryCode);
        setCountryId(data[0]?.id);
        setStateList([]);
        setStateId(0);
        GetState(data[0]?.id)?.then((result: any) => {
            setStateList(result);
        });
    }, [userCountryCode, countriesList]);
    useEffect(() => {
        const data: any = stateList.filter((i: any) => i.state_code === userStateCode);
        setStateId(data[0]?.id);
    }, [stateList, userStateCode]);

    const updateInstallerAPI = async (formData: IFormObj) => {
        try {
            dispatch(showLoader());

            const input: any = {
                first_name: formData.firstName,
                last_name: formData.lastName,
                license_number: formData.licenseNo,
                status: formData.status,
                cognito_id: formData.cognitoId,
                user_id: formData.id,
                // address: {
                //     country: getCountryCode()?.toString(),
                //     state: formData.state,
                //     city: getStateCode()?.toString(),
                //     street: formData.street,
                //     zip_code: formData.zipCode,
                // },
            };
            const { data } = await client.mutate({
                mutation: UPDATE_INSTALLER,
                variables: { input },
            });

            console.log('Update Installer response:', data);

            dispatch(
                openToast({
                    show: true,
                    message: data?.updateInstaller?.message,
                    severity: 'success',
                    isAlertOpen: true,
                }),
            );
            onClose();
            fetchMangeInstallerData();
        } catch (err: any) {
            console.error('Update Installer failed', err);
            if (err.graphQLErrors && err.graphQLErrors[0]?.extensions?.code === '403') {
                // Handle forbidden: show message, redirect, etc.
                // console.log('Received Forbidden error:', err);
                if (canView(FunctionsEnum.MANAGE_INSTALLERS)) {
                    navigate(NavigationConfig.ManageInstaller);
                } else {
                    navigate(NavigationConfig.Home);
                }
                onClose();
            } else {
                // Handle other errors
                console.error('Other error:', err);
            }
        } finally {
            dispatch(hideLoader());
        }
    };

    const handleToggle = (checked: boolean) => {
        const status = checked ? userEnums.Status.active : userEnums.Status.inactive;
        setNewStatus(status);
        setModalOpen(true);
    };

    const handleConfirmStatusChange = (setFieldValue: any) => {
        setFieldValue('status', newStatus);
        setModalOpen(false);
    };

    const formatPhoneNumber = (number: string, countryCode: string) => {
        const digits = number.replace(/\D/g, '');
        if (countryCode === '+1' || countryCode === '+91') {
            return digits.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
        }

        return digits;
    };

    useEffect(() => {
        GetCountries()?.then((result: any) => {
            const filteredCountries = result
                .filter((country: any) => country.name === 'India' || country.name === 'United States')
                .map((country: any) => ({
                    ...country,
                    phone_code: `+${country.phone_code}`,
                }));

            setCountriesList(filteredCountries);
        });
    }, []);

    useEffect(() => {
        if (installer?.phone_number) {
            const countryIso2 = installer?.user_addresses?.[0]?.country || 'IN';
            const country = countriesList.find(c => c.iso2 === countryIso2);

            if (country) {
                setSelectedCountryPhoneCode(country.phone_code);
                const rawNumber = installer.phone_number.replace(country.phone_code, '').trim();
                setPhoneNumber(formatPhoneNumber(rawNumber, country.phone_code));
            } else {
                setSelectedCountryPhoneCode('');
                setPhoneNumber(installer.phone_number);
            }
        }
    }, [installer, countriesList]);

    useEffect(() => {
        if (installer?.id) {
            installerDetails();
        }
    }, [installer, installerDetails]);
    const handleCountryChange = (e: any) => {
        setCountryId(e?.id);
        GetState(e?.id).then((result: any) => {
            setStateList(result);
        });
    };
    const handleStateChange = (e: any) => {
        setStateId(e?.id);
    };
    return (
        <Box>
            <Typography px={4} pt={4} variant="h4">
                {t('edit_installer')}
            </Typography>
            <MainWrapper className="detailsForm cstUpdateInstaller">
                <Box>
                    {installerData && (
                        <Formik
                            initialValues={installerData}
                            validationSchema={UpdateInstallerValidationSchema(t)}
                            onSubmit={async values => {
                                await updateInstallerAPI(values);
                            }}
                            enableReinitialize
                        >
                            {({ values, setFieldValue, submitForm, resetForm, errors }) => {
                                console.log(errors);
                                return (
                                    <Form>
                                        <Box
                                            height="calc(100dvh - 136px)"
                                            py={1.5}
                                            overflow="auto"
                                            px={4}
                                            ref={scrollRef}
                                        >
                                            <Grid container flexGrow={1} className="details profileDetails" spacing={2}>
                                                <Grid size={{ xs: 12, sm: 6 }}>
                                                    <Stack>
                                                        <Typography variant="subtitle1" mb={1} color="info.100">
                                                            {t('first_name')}
                                                        </Typography>
                                                        <TextFieldComponent
                                                            name="firstName"
                                                            placeholder={t('enter_first_name')}
                                                            onChange={(e: { target: { value: any } }) =>
                                                                setFieldValue('firstName', e.target.value)
                                                            }
                                                            value={values?.firstName}
                                                        />
                                                        <FormHelperText error>
                                                            <ErrorMessage name="firstName" />
                                                        </FormHelperText>
                                                    </Stack>
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: 6 }}>
                                                    <Stack>
                                                        <Typography variant="subtitle1" mb={1} color="info.100">
                                                            {t('last_name')}
                                                        </Typography>
                                                        <TextFieldComponent
                                                            name="lastName"
                                                            placeholder={t('enter_last_name')}
                                                            onChange={(e: { target: { value: any } }) =>
                                                                setFieldValue('lastName', e.target.value)
                                                            }
                                                            value={values?.lastName}
                                                        />
                                                        <FormHelperText error>
                                                            <ErrorMessage name="lastName" />
                                                        </FormHelperText>
                                                    </Stack>
                                                </Grid>
                                                <Grid size={{ xs: 12 }}>
                                                    <Stack>
                                                        <Typography variant="subtitle1" mb={1} color="info.100">
                                                            {t('license_no')}
                                                        </Typography>
                                                        <TextFieldComponent
                                                            name="licenseNo"
                                                            placeholder={t('enter_license_no')}
                                                            onChange={(e: { target: { value: any } }) =>
                                                                setFieldValue('licenseNo', e.target.value)
                                                            }
                                                            value={values?.licenseNo}
                                                        />
                                                        <FormHelperText error>
                                                            <ErrorMessage name="licenseNo" />
                                                        </FormHelperText>
                                                    </Stack>
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: 6 }} display="flex" alignItems="flex-end">
                                                    <Stack width="100%">
                                                        <FormControl className="cstSelectProfile cstState">
                                                            <Typography variant="subtitle1" color="info.100">
                                                                {t('country')}
                                                                <Box component="span" color={colors.astric}>
                                                                    *
                                                                </Box>
                                                            </Typography>
                                                            <Autocomplete
                                                                options={countriesList}
                                                                getOptionLabel={(option: any) => option.name || ''}
                                                                value={
                                                                    selectedCountryId
                                                                        ? countriesList.find(
                                                                              (country: any) =>
                                                                                  country.id === selectedCountryId,
                                                                          )
                                                                        : ''
                                                                }
                                                                disableClearable
                                                                onChange={(event, newValue) =>
                                                                    handleCountryChange(newValue)
                                                                }
                                                                renderInput={params => (
                                                                    <TextField
                                                                        {...params}
                                                                        inputProps={{
                                                                            ...params.inputProps,
                                                                            readOnly: true,
                                                                        }}
                                                                        placeholder={t('select_country')}
                                                                        InputLabelProps={{ sx: { display: 'none' } }}
                                                                    />
                                                                )}
                                                            />
                                                        </FormControl>
                                                        <FormHelperText error>
                                                            <ErrorMessage name="country" />
                                                        </FormHelperText>
                                                    </Stack>
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: 6 }} display="flex" alignItems="flex-end">
                                                    <Stack width="100%">
                                                        <FormControl className="cstSelectProfile cstState">
                                                            <Typography variant="subtitle1" color="info.100">
                                                                {t('state')}
                                                                <Box component="span" color={colors.astric}>
                                                                    *
                                                                </Box>
                                                            </Typography>
                                                            <Autocomplete
                                                                options={stateList}
                                                                getOptionLabel={(option: any) => option.name || ''} // Specify the display label
                                                                value={
                                                                    selectedStateId
                                                                        ? stateList.find(
                                                                              (state: any) =>
                                                                                  state.id === selectedStateId,
                                                                          )
                                                                        : ''
                                                                }
                                                                onChange={(event, newValue) =>
                                                                    handleStateChange(newValue)
                                                                } // Handle selection
                                                                renderInput={params => (
                                                                    <TextField
                                                                        {...params}
                                                                        placeholder={t('select_state')}
                                                                        InputLabelProps={{ sx: { display: 'none' } }}
                                                                    />
                                                                )}
                                                            />
                                                        </FormControl>
                                                        <FormHelperText error>
                                                            <ErrorMessage name="state" />
                                                        </FormHelperText>
                                                    </Stack>
                                                </Grid>
                                                <Grid size={{ xs: 12 }}>
                                                    <Stack className="cstVerify cstMobileNumber">
                                                        <Typography variant="subtitle1" mb={1} color="info.100">
                                                            {t('mobile_no')}
                                                        </Typography>

                                                        <Box display="flex" gap={1}>
                                                            {/* Country Code (Non-Editable Select) */}
                                                            <FormControl
                                                                className="cstSelectProfile cstEmail"
                                                                sx={{ width: '20%' }}
                                                            >
                                                                <Select
                                                                    labelId="demo-simple-select-label"
                                                                    id="demo-simple-select"
                                                                    label="CountryCode"
                                                                    // placeholder={`${t('country_code')}`}
                                                                    value={selectedCountryPhoneCode}
                                                                    className="cstEditProfile cstDisableEmail"
                                                                    disabled
                                                                >
                                                                    {countriesList?.map((item: any) => (
                                                                        <MenuItem key={item.id} value={item.phone_code}>
                                                                            {item?.phone_code}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                            <Stack className="cstEmail" width="80%">
                                                                <TextFieldComponent
                                                                    variant="outlined"
                                                                    size="small"
                                                                    name="phoneNumber"
                                                                    type="string"
                                                                    placeholder={`${t('enter_mobile_number')}`}
                                                                    value={phoneNumber}
                                                                    disabled
                                                                    error={false}
                                                                    className="cstDisableEmail"
                                                                />
                                                                <FormHelperText error>
                                                                    <ErrorMessage name="mobileNo" />
                                                                </FormHelperText>
                                                            </Stack>
                                                        </Box>
                                                    </Stack>
                                                </Grid>

                                                <Grid size={{ xs: 12 }}>
                                                    <Stack className="cstEmail">
                                                        <Typography variant="subtitle1" mb={1} color="info.100">
                                                            {t('email')}
                                                        </Typography>
                                                        <TextFieldComponent
                                                            name="email"
                                                            disabled
                                                            value={values?.email}
                                                            className="cstDisableEmail"
                                                        />
                                                    </Stack>
                                                    <FormHelperText error>
                                                        <ErrorMessage name="email" />
                                                    </FormHelperText>
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: 6 }}>
                                                    <Stack>
                                                        <Typography variant="subtitle1" mb={1} color="info.100">
                                                            {t('city')}{' '}
                                                            <Box component="span" color={colors.astric}>
                                                                *
                                                            </Box>
                                                        </Typography>
                                                        <TextFieldComponent
                                                            variant="outlined"
                                                            id=""
                                                            error={false}
                                                            size="small"
                                                            isAutoFocus
                                                            name="city"
                                                            type="text"
                                                            placeholder={`${t('enter_city')}`}
                                                            onChange={(e: { target: { value: any } }) =>
                                                                setFieldValue('city', e.target.value)
                                                            }
                                                            value={values?.city}
                                                        />
                                                        <FormHelperText error>
                                                            <ErrorMessage name="city" />
                                                        </FormHelperText>
                                                    </Stack>
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: 6 }}>
                                                    <Stack>
                                                        <Typography variant="subtitle1" mb={1} color="info.100">
                                                            {t('building_address')}{' '}
                                                            <Box component="span" color={colors.astric}>
                                                                *
                                                            </Box>
                                                        </Typography>
                                                        <TextFieldComponent
                                                            variant="outlined"
                                                            id=""
                                                            error={false}
                                                            size="small"
                                                            isAutoFocus
                                                            name="street"
                                                            type="text"
                                                            placeholder={`${t('enter_address')}`}
                                                            onChange={(e: { target: { value: any } }) =>
                                                                setFieldValue('street', e.target.value)
                                                            }
                                                            value={values?.street}
                                                        />
                                                        <FormHelperText error>
                                                            <ErrorMessage name="street" />
                                                        </FormHelperText>
                                                    </Stack>
                                                </Grid>
                                                <Grid size={12} pb={3}>
                                                    <Stack>
                                                        <Typography variant="subtitle1" mb={1} color="info.100">
                                                            {t('zip_code')}
                                                            <Box component="span" color={colors.astric}>
                                                                *
                                                            </Box>
                                                        </Typography>
                                                        <TextFieldComponent
                                                            variant="outlined"
                                                            id=""
                                                            error={false}
                                                            size="small"
                                                            isAutoFocus
                                                            name="zipCode"
                                                            type="text"
                                                            placeholder={`${t('enter_zip_code')}`}
                                                            onChange={(e: { target: { value: any } }) =>
                                                                setFieldValue('zipCode', e.target.value)
                                                            }
                                                            value={values?.zipCode}
                                                        />
                                                        <FormHelperText error>
                                                            <ErrorMessage name="zipCode" />
                                                        </FormHelperText>
                                                    </Stack>
                                                </Grid>
                                                <Grid size={{ xs: 12 }}>
                                                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                                        <Typography>{t('user_status')}</Typography>
                                                        <AntSwitch
                                                            checked={values?.status === userEnums.Status.active}
                                                            onChange={e => handleToggle(e.target.checked)}
                                                            disabled={!canEdit(FunctionsEnum.MANAGE_INSTALLERS)}
                                                        />
                                                    </Stack>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                        <Stack
                                            direction={{ xs: 'column', sm: 'row' }}
                                            alignItems="center"
                                            justifyContent="space-between"
                                            className="actionFooter"
                                            spacing={2}
                                            pt={2}
                                        >
                                            <ButtonComponent
                                                size="medium"
                                                variant="contained"
                                                fullWidth
                                                buttonName={t('cancel')}
                                                className="cstBtn"
                                                type="button"
                                                onClick={() => {
                                                    resetForm();
                                                    onClose();
                                                }}
                                            />
                                            <ButtonComponent
                                                size="medium"
                                                variant="contained"
                                                fullWidth
                                                buttonName={t('save')}
                                                className="cstBtn"
                                                type="button"
                                                onClick={submitForm}
                                                disabled={!canEdit(FunctionsEnum.MANAGE_INSTALLERS)}
                                            />
                                        </Stack>
                                        <CommonModal
                                            modalOpen={modalOpen}
                                            header={`Do you want to ${newStatus === userEnums.Status.active ? 'activate' : 'inactivate'} this installer?`}
                                            cancelBtnName="Cancel"
                                            AcceptBtnName="Yes"
                                            closePopup={() => setModalOpen(false)}
                                            acceptBtn={() => handleConfirmStatusChange(setFieldValue)}
                                        />
                                    </Form>
                                );
                            }}
                        </Formik>
                    )}
                </Box>
            </MainWrapper>
        </Box>
    );
};
